import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { MapPin, TrendingUp, Users, DollarSign, Award, BarChart3, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/shared/SeoHead";
import PageHero, { HeroStatTile } from "@/components/shared/PageHero";
import { AdSense, LeaderboardAd, InContentAd, SidebarAd } from "@/components/shared/AdSense";
import AdFrame from "@/components/shared/AdFrame";
import SalaryTable from "@/components/shared/SalaryTable";
import RppToggle from "@/components/shared/RppToggle";
import AffiliateCta from "@/components/shared/AffiliateCta";
import MetricNavigation from "@/components/shared/MetricNavigation";
import RelatedMetrics from "@/components/shared/RelatedMetrics";
import { supabase } from "@/integrations/supabase/client";
import { isValidState, getStateByName } from "@/lib/stateUtils";
import { shouldIndexBestStatesPage } from "@/lib/seoIndexing";
import { useOccupationResolver } from "@/hooks/useOccupationResolver";
import NotFound from "./NotFound";

const BestStates = () => {
  const { occupation } = useParams<{ occupation: string }>();
  const navigate = useNavigate();
  const [useRpp, setUseRpp] = useState(false);

  // Handle state redirects - if someone tries to access /best-states/california
  useEffect(() => {
    if (occupation && isValidState(occupation)) {
      navigate(`/state/${occupation}/`, { replace: true });
      return;
    }
  }, [occupation, navigate]);

  const occupationSlug = occupation || 'software-developer';

  const { data: resolvedOccupation, isLoading: isResolvingOccupation, isError: isResolveError } = useOccupationResolver(occupationSlug);

  const currentOccupation = resolvedOccupation
    ? { soc: resolvedOccupation.soc, title: resolvedOccupation.title, description: resolvedOccupation.description }
    : { soc: '15-1252', title: 'Software Developer', description: 'Design and develop software applications and systems' };

  // Fetch best states data (only when occupation is resolved)
  const { data: statesData, isLoading } = useQuery({
    queryKey: ['best-states', currentOccupation.soc, useRpp],
    enabled: (!!resolvedOccupation || !occupation) && !isResolvingOccupation,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('api-best-states', {
        body: {
          soc: currentOccupation.soc,
          rpp: useRpp,
          limit: 20,
          year: 2025
        }
      });
      
      if (error) throw error;
      return data;
    },
    // Ensure we refetch when useRpp changes
    refetchOnMount: true,
    refetchOnWindowFocus: false
  });

  // Calculate summary stats (some summary-level SOC codes have no state rows,
  // so an empty result must not produce a stats object with topState undefined)
  const summaryStats = statesData?.data?.length ? (() => {
    const data = statesData.data;
    const salaries = data.map((state: unknown) => useRpp ? (state.mean_salary_real || state.mean_salary) : state.mean_salary).filter(Boolean);
    const employment = data.map((state: unknown) => state.employment || 0);
    
    return {
      topState: data[0],
      avgSalary: Math.round(salaries.reduce((sum: number, sal: number) => sum + sal, 0) / salaries.length),
      totalJobs: employment.reduce((sum: number, emp: number) => sum + emp, 0),
      stateCount: data.length,
      salaryRange: {
        min: Math.min(...salaries),
        max: Math.max(...salaries)
      }
    };
  })() : null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const baseUrl = 'https://wageatlas.com';
  const canonicalUrl = occupation
    ? `${baseUrl}/best-states/${occupationSlug}/`
    : `${baseUrl}/best-states/`;

  const pageTitle = resolvedOccupation
    ? `Highest Paying States for ${resolvedOccupation.title} (${new Date().getFullYear()}) | Wage Atlas`
    : `Highest Paying States by Occupation (${new Date().getFullYear()}) | Wage Atlas`;
  const pageDescription = resolvedOccupation
    ? `Where do ${resolvedOccupation.title} earn the most? ${summaryStats?.topState?.area_name || 'Top states'} leads at ${formatCurrency(summaryStats?.topState?.mean_salary || 85000)} average. Compare salaries across all 50 states with BLS OEWS data.`
    : 'Find the highest paying states for any occupation. Compare average salaries, employment, and cost-of-living-adjusted pay across all 50 states.';

  if (isResolveError && occupation) {
    return <NotFound />;
  }

  if (isLoading || isResolvingOccupation) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3 space-y-6">
              <div className="h-8 bg-muted animate-pulse rounded" />
              <div className="h-32 bg-muted animate-pulse rounded" />
              <div className="h-96 bg-muted animate-pulse rounded" />
            </div>
            <div className="space-y-4">
              <div className="h-64 bg-muted animate-pulse rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={pageTitle}
        description={pageDescription}
        occupation={currentOccupation.title}
        canonical={canonicalUrl}
        noIndex={!!occupation && !shouldIndexBestStatesPage(occupation)}
      />

      {/* Hero Section */}
      <PageHero
        badge={{ icon: MapPin, label: 'State-by-State Analysis' }}
        title={<>Best States for {currentOccupation.title}</>}
        subtitle="Compare salaries, employment, and opportunities across all 50 states"
      >
        {/* Top ad placement */}
        <AdFrame className="flex justify-center mb-8">
          <LeaderboardAd className="max-w-4xl" />
        </AdFrame>

        {summaryStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <HeroStatTile icon={Award} value={summaryStats.topState?.area_name || 'N/A'} label="Top State" />
            <HeroStatTile icon={DollarSign} value={formatCurrency(summaryStats.avgSalary)} label="Avg Salary" />
            <HeroStatTile icon={Users} value={formatNumber(summaryStats.totalJobs)} label="Total Jobs" />
            <HeroStatTile
              icon={BarChart3}
              value={`$${Math.round((summaryStats.salaryRange.max - summaryStats.salaryRange.min) / 1000)}k`}
              label="Salary Range"
            />
          </div>
        )}
      </PageHero>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content Area */}
          <div className="lg:col-span-3 space-y-6">
            
            {/* State Rankings */}
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <CardTitle className="text-xl font-semibold">
                    State Rankings by Salary
                  </CardTitle>
                  <div className="flex items-center gap-4">
                    <RppToggle
                      enabled={useRpp}
                      onToggle={setUseRpp}
                    />
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">
                  {useRpp ? 'Salaries adjusted for regional cost of living (purchasing power)' : 'Raw salary data without cost-of-living adjustments'}
                </p>
                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-xs text-blue-800 dark:text-blue-200 flex items-center gap-2">
                    <HelpCircle className="h-4 w-4 flex-shrink-0" />
                    <span>
                      Data organized by SOC code {currentOccupation.soc}.{' '}
                      <Link to="/what-is-soc-code/" className="underline hover:text-blue-600 dark:hover:text-blue-300">
                        Learn about SOC codes
                      </Link>
                    </span>
                  </p>
                </div>
              </CardHeader>
              <CardContent>
                {statesData?.data && statesData.data.length > 0 ? (
                  <SalaryTable
                    firstColumnLabel="State"
                    data={statesData.data.map((state: Record<string, number | string | undefined>, index: number) => ({
                      rank: index + 1,
                      occ_title: String(state.area_name || 'Unknown State'),
                      soc: String(state.area_code || ''),
                      annual_mean_wage: Number(useRpp ? (state.mean_salary_real || state.mean_salary || 0) : (state.mean_salary || 0)),
                      annual_median_wage: Number(useRpp ? (state.median_salary_real || state.median_salary || 0) : (state.median_salary || 0)),
                      hourly_mean_wage: Number(useRpp ? (state.hourly_mean_real || ((Number(state.mean_salary) || 0) / 2080)) : ((Number(state.mean_salary) || 0) / 2080)),
                      total_employment: Number(state.employment || 0),
                      mean_salary: Number(useRpp ? (state.mean_salary_real || state.mean_salary || 0) : (state.mean_salary || 0)),
                      median_salary: Number(useRpp ? (state.median_salary_real || state.median_salary || 0) : (state.median_salary || 0)),
                      hourly_mean: Number(useRpp ? (state.hourly_mean_real || ((Number(state.mean_salary) || 0) / 2080)) : ((Number(state.mean_salary) || 0) / 2080)),
                      tot_emp: Number(state.employment || 0),
                      rpp_factor: state.rpp_factor as number | undefined,
                    }))}
                    getRowHref={(row) => {
                      if (!occupationSlug) return null;
                      const mapped = getStateByName(row.occ_title);
                      if (!mapped) return null;
                      return `/salary/${occupationSlug}/${mapped.slug}/`;
                    }}
                    showAffiliateEvery={5}
                    area="United States"
                    className="shadow-none border-0"
                  />
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No state comparison data available for {currentOccupation.title}.
                  </div>
                )}

                {/* Relocation callout */}
                <div className="mt-8 p-6 bg-gradient-to-r from-accent/10 to-accent/5 rounded-lg border border-accent/20">
                  <div className="flex items-start gap-4">
                    <MapPin className="h-6 w-6 text-accent mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">Move Smart, Earn More</h3>
                      </div>
                      <p className="text-muted-foreground mb-4">
                        Compare cost of living against salary across states, then explore open roles. 
                        All of our salary tools and comparisons are free to use.
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <Button 
                          className="bg-accent hover:bg-accent/90"
                          onClick={() => navigate('/calculators/')}
                        >
                          Explore Salary Calculators
                        </Button>
                        <AffiliateCta
                          provider="indeed"
                          occupation={currentOccupation.title}
                          location="United States"
                          type="job-search"
                          className="border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* In-content ad */}
                <InContentAd />
              </CardContent>
            </Card>

            {/* Insights & Analysis */}
            <Card>
              <CardHeader>
                <CardTitle>Market Analysis & Insights</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm prose-neutral dark:prose-invert max-w-none">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-3 text-primary">Top Markets</h4>
                    {summaryStats ? (
                      <div className="space-y-3">
                        <p className="text-muted-foreground">
                          {summaryStats.topState?.area_name} leads in {currentOccupation.title} salaries 
                          with {formatCurrency(useRpp ? (summaryStats.topState.mean_salary_real || summaryStats.topState.mean_salary) : summaryStats.topState.mean_salary)} 
                          {useRpp ? ' in purchasing power' : ' average compensation'}.
                        </p>
                        <ul className="space-y-2">
                          <li className="flex items-start gap-2">
                            <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                            <span>High concentration of tech companies</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                            <span>Strong job market demand</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
                            <span>Competitive benefit packages</span>
                          </li>
                        </ul>
                      </div>
                    ) : (
                      <p className="text-muted-foreground">Market analysis will be displayed when data is available.</p>
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-wage-accent">Decision Factors</h4>
                    <p className="text-muted-foreground mb-4">
                      When choosing where to work as a {currentOccupation.title}, consider both 
                      salary and cost of living. The {useRpp ? 'adjusted' : 'raw'} salary data 
                      {useRpp ? ' accounts for regional purchasing power' : ' shows market rates before cost adjustments'}.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-wage-accent rounded-full mt-2"></div>
                        <span>Cost of living variations</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-wage-accent rounded-full mt-2"></div>
                        <span>Job market competition</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-wage-accent rounded-full mt-2"></div>
                        <span>Industry concentration</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-wage-accent rounded-full mt-2"></div>
                        <span>Remote work policies</span>
                      </li>
                    </ul>
                  </div>
                </div>
                
                <Separator className="my-6" />
                
                <p className="text-muted-foreground">
                  State rankings are based on {statesData?.rpp_adjusted ? 'purchasing power adjusted' : 'nominal'} 
                  salaries using {new Date().getFullYear()} Bureau of Labor Statistics data. 
                  Employment figures reflect full-time equivalent positions.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <MetricNavigation
              occupation={{
                title: currentOccupation.title,
                occ_title: currentOccupation.title,
                soc: currentOccupation.soc,
                soc_code: currentOccupation.soc,
              }}
              location="united-states"
              currentMetric="best-states"
              collapsible={false}
            />
            
            <RelatedMetrics
              occupation={{
                title: currentOccupation.title,
                occ_title: currentOccupation.title,
                soc: currentOccupation.soc,
                soc_code: currentOccupation.soc,
              }}
              location="united-states"
              currentMetric="best-states"
              variant="compact"
            />
            
            {/* Sidebar Ad */}
            <SidebarAd />

            {/* Related Occupations */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Compare Other Careers</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {[
                  { slug: 'software-developers', title: 'Software Developer' },
                  { slug: 'registered-nurses', title: 'Registered Nurse' },
                  { slug: 'accountants-and-auditors', title: 'Accountant' },
                  { slug: 'financial-analysts-and-advisors', title: 'Financial Analyst' },
                  { slug: 'elementary-and-middle-school-teachers', title: 'Teacher' },
                  { slug: 'management-analysts', title: 'Management Analyst' },
                ].map((occ) => (
                  <Button 
                    key={occ.slug}
                    variant={occ.slug === occupationSlug ? "default" : "outline"} 
                    className="w-full justify-start text-sm"
                    onClick={() => navigate(`/best-states/${occ.slug}/`)}
                  >
                    {occ.title}
                  </Button>
                ))}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Career Tools</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate(`/trends/${occupationSlug}/united-states/`)}
                >
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Salary Trends
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate(`/calculators/`)}
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Salary Calculator
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate(`/salary/${occupationSlug}/california/`)}
                >
                  <MapPin className="h-4 w-4 mr-2" />
                  Detailed Analysis
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BestStates;